import _ from "lodash";
import queryString from "query-string";
import normalize from "json-api-normalizer";
import React, { useMemo, useRef, useState, useEffect } from "react";
import { useParams, Redirect, useHistory } from "react-router-dom";
import { Label, Message, Segment, Button, Icon, Header } from "semantic-ui-react";
import CurrencyFormat from "react-currency-format";
import { useQuery, useMutation, useQueryCache } from "react-query";
import { useToasts } from "react-toast-notifications";
import { VZenTable } from "../../../../components/VZenTable";
import { SafeMoment } from "../../../../components/SafeMoment";
import { PublicWrapper } from "../../../../components/PublicWrapper";
import { getPermits, postNotRenewing } from "../../../../queries/permits";
import { getPermitsTypes } from "../../../../queries/permitsTypes";
import identitySelectors from "../../../../selectors/identity";
import { NotReNewingModal } from "../../../../apps/pages/NotReNewingModal";
import PlaceholderUntilLoaded from "../../../../components/global_components/PlaceholderUntilLoaded";
import { openContextMenu } from "../../../../utils/functions";
import CustomContextMenu from "../../../../components/global_components/CustomContextMenu";
import { defaultTableRowRenderer } from "react-virtualized";
import "./PermitsCitizenList.scss";
import CitizenInspectionList from "../../components/inspections/citizen-inspection-list/CitizenInspectionList";
import usePermitType from "../../hooks/permits/usePermitType";
import useNetworkHeaders from "../../../../hooks/useNetworkHeaders";

type DashboardListTab = (typeof DashboardListTabs)[keyof typeof DashboardListTabs];
const DashboardListTabs = {
  ALL_PERMITS: 0,
  ISSUED_PERMITS: 1,
  INSPECTIONS: 2,
} as const;

export const CitizenList = () => {
  const { site } = useParams<{ site: string }>();
  const params = queryString.parse(location.search) as any;
  const [reasons, setReasons] = useState<boolean>(false);
  const [permitId, setPermitId] = useState<number>(-1);
  const [notReNewingModalState, setNotReNewingModalState] = useState<boolean>(false);
  const contentRef = useRef(null);
  const networkHeaders = useNetworkHeaders();
  const isLoggedIn = identitySelectors.isLoggedIn();
  const history = useHistory();
  const applicationQuery = useQuery(["citizenApplication"], getPermits(networkHeaders, { creatorId: "me" }));

  const permitsQuery = useQuery(["citizenPermits"], getPermits(networkHeaders, { creatorId: "me", issued: "true" }));
  const permitsTypesQuery = useQuery(["permitsTypes"], getPermitsTypes(networkHeaders));
  const normalizedPermitsTypes = useMemo(() => normalize(permitsTypesQuery.data || []), [permitsTypesQuery.data]);

  const [activeDashboardList, setActiveDashboardList] = useState<DashboardListTab>(DashboardListTabs.ALL_PERMITS);
  const dashboardTabRefs = useRef<Record<DashboardListTab, HTMLButtonElement | null>>({
    0: null,
    1: null,
    2: null,
  });

  const [contextMenuLink, setContextMenuLink] = useState("");
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 });
  const [showPopup, setShowPopup] = useState(false);
  const handleContextMenu = (event: any) => {
    openContextMenu(event, setPopupPosition, setShowPopup);
  };

  const [notRenewingMutation] = useMutation(postNotRenewing(networkHeaders), {
    onSuccess: () => queryCache.invalidateQueries("citizenPermits"),
  });
  const initialState = {};
  const { addToast } = useToasts();
  const queryCache = useQueryCache();

  useEffect(() => {
    document.title = "My Dashboard";
  }, []);

  const tableProps = {
    headerHeight: 50,
    rowHeight: 80,
    // Adding custom row renderer to allow for tabbing through the confirmation numbers, which are accessible links
    rowRenderer: (data: any) => {
      const id = data.rowData.original.id;
      const url = `/${site}/public/permits/details/${id}/application`;
      const rightClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        setContextMenuLink(`/${site}/public/permits/details/${id}/application`);
        handleContextMenu(e);
      };
      const onRowClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (e.metaKey || e.ctrlKey) {
          window.open(url, "_blank");
        } else {
          history.push(url);
        }
      };

      return (
        //Removed the onRowClick props from VZenTable, and wrapped the row in this div with onClicks, to stop the row itself from being focusable
        <div
          key={id}
          role='presentation'
          onClick={(event) => onRowClick(event)}
          onContextMenu={(event) => rightClick(event)}
        >
          {defaultTableRowRenderer(data)}
        </div>
      );
    },
  };

  const applicationRows = useMemo(
    () =>
      _.get(applicationQuery.data, "data", []).map((permit: any) => {
        return Object.assign({}, { id: permit.id }, permit.attributes, {
          needsPayment: permit.attributes["total-paid"] < permit.attributes["total-fees"],
          isPending: permit.attributes["total-pending"] && permit.attributes["total-pending"] > 0,
        });
      }),
    [applicationQuery.data],
  );
  const permitsRows = useMemo(
    () =>
      _.get(permitsQuery.data, "data", []).map((permit: any) => {
        return Object.assign({}, { id: permit.id }, permit.attributes, {
          needsPayment: permit.attributes["total-paid"] < permit.attributes["total-fees"],
          isPending: permit.attributes["total-pending"] && permit.attributes["total-pending"] > 0,
        });
      }),
    [permitsQuery.data],
  );

  const noApplicationRows = activeDashboardList === DashboardListTabs.ALL_PERMITS && applicationRows.length === 0;
  const noPermitsRows = activeDashboardList === DashboardListTabs.ISSUED_PERMITS && permitsRows.length === 0;

  const containerProps = {
    style: {
      display: "block",
      minHeight: DashboardListTabs.ALL_PERMITS
        ? Math.max(300, applicationQuery?.data?.data.length * 90 + 50)
        : Math.max(300, permitsQuery?.data?.data.length * 90 + 50),
      minWidth: 900,
    },
  };

  const renderConfirmationCell = (data: any) => {
    const confirmationNumber = data.row.original["confirmation-number"];
    const formattedConfirmationNumber = (confirmationNumber || "").match(/.{3}/g)?.join("-");
    const id = data.row.original.id;
    const url = `/${site}/public/permits/details/${id}#Application`;
    return (
      <a style={{ color: "black" }} aria-describedby={`type-${id}`} href={url}>
        {data.value || formattedConfirmationNumber || ""}
      </a>
    );
  };

  const renderPermitTypeCell = (data: any) => {
    const originalId = data.row.original.id;
    const id = data.row.original["permits-type-id"];
    const identifier = data.row.original["primary-identifier"];
    const permitType = usePermitType({ permitTypeId: id });
    const permitsTypeName = useMemo(() => _.get(permitType.data, "data.attributes.name"), [permitType.data]);
    return (
      <>
        <p id={`type-${originalId}`} className='tw-mb-1'>
          {" "}
          {permitsTypeName}{" "}
        </p>
        <p className='cellSubHeader subText'> {identifier} </p>
      </>
    );
  };

  const columns = useMemo(
    () => [
      {
        accessor: "reservation-number",
        Header: "#",
        Cell: (data: any) => {
          return renderConfirmationCell(data);
        },
        style: {
          border: 0,
        },
      },
      {
        Header: "TYPE",
        width: "500",
        Cell: (data: any) => {
          return renderPermitTypeCell(data);
        },
        style: {
          border: 0,
        },
      },
      {
        accessor: "status",
        Header: "STATUS",
        Cell: (data: any) => {
          const statusMap = {
            open: (
              <Label size='large' circular basic>
                Open
              </Label>
            ),
            on_hold: (
              <Label size='large' circular color='red'>
                On Hold
              </Label>
            ),
            closed: (
              <Label size='large' circular color='red'>
                Closed
              </Label>
            ),
            completed: (
              <Label size='large' circular color='green'>
                Completed
              </Label>
            ),
            in_progress: (
              <Label size='large' circular color='blue'>
                In Progress
              </Label>
            ),
          } as any;
          return (
            statusMap[data.value as string] || (
              <Label circular size='large' color='orange' className='capitalize'>
                {data.value}
              </Label>
            )
          );
        },
      },
      {
        accessor: "created-at",
        Header: "CREATED",
        Cell: (data: any) => <SafeMoment format='MMM D, YYYY'>{data.value}</SafeMoment>,
      },
      {
        accessor: "total-fees",
        Header: "BALANCE",
        width: "90",
        Cell: (data: any) => {
          if (data.value === null) {
            return "$0.00";
          }
          return (
            <div className='align-right'>
              <CurrencyFormat
                className='align-right'
                value={data.row.original["requested-balance-due"] / 100 || 0}
                fixedDecimalScale={true}
                decimalScale={2}
                displayType={"text"}
                thousandSeparator={true}
                prefix={"$"}
              />
            </div>
          );
        },
      },
    ],
    [normalizedPermitsTypes],
  );

  const reNew = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, id: number, parentId: number) => {
    e.stopPropagation();
    window.location.href = `/${site}/public/permits/apply?permitsTypeId=${id}&parentId=${parentId}`;
  };

  const notRenewing = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, data: any, id: number) => {
    e.stopPropagation();
    setReasons(data);
    setPermitId(id);
    setNotReNewingModalState(true);
  };

  const closeNotReNewingModal = () => {
    setNotReNewingModalState(false);
  };

  const onSubmit = (data: any) => {
    const nonRenewalReason = data["reason"] === "Other" ? data["note"] : data["reason"];
    notRenewingMutation({
      data: { nonRenewalReason },
      id: permitId || "",
    });
    setNotReNewingModalState(false);
    addToast(`Marked as not renewing`, { appearance: "success", autoDismiss: true });
  };

  const permitColumns = useMemo(
    () => [
      {
        accessor: "reservation-number",
        Header: "#",
        width: 240,
        style: {
          border: 0,
        },
        Cell: (data: any) => {
          return renderConfirmationCell(data);
        },
      },
      {
        accessor: "permits-type-id",
        Header: "TYPE",
        width: "400",
        Cell: (data: any) => {
          return renderPermitTypeCell(data);
        },
        style: {
          border: 0,
        },
      },
      {
        accessor: "issued-at",
        Header: "ISSUED",
        width: "200",
        Cell: (data: any) => <SafeMoment format='MMM D, YYYY'>{data.value}</SafeMoment>,
      },
      {
        accessor: "expired-at",
        Header: "Expires",
        width: "200",
        Cell: (data: any) => (
          <SafeMoment format='MMM D, YYYY' utc={true}>
            {data.value}
          </SafeMoment>
        ),
      },
      {
        accessor: "renewal-status",
        Header: "Renewal Status",
        width: "400",
        Cell: (data: any) => {
          const id = data.row.original["permits-type-id"];
          const permitType = usePermitType({ permitTypeId: id });
          const nonRenewalReasons = useMemo(
            () => _.get(permitType.data, "data.attributes.non-renewal-reasons"),
            [permitType.data],
          );
          let renewalWindowStartedAt;
          if (data.row.original["renewal-status"] === "ineligible") {
            if (data.row.original["renewal-window-started-at"]) {
              renewalWindowStartedAt = new Date(data.row.original["renewal-window-started-at"]).toLocaleDateString();
            }
          }
          return (
            <div className='action-container'>
              {data.row.original["renewal-status"] === "eligible" && (
                <Button
                  primary
                  size='tiny'
                  onClick={(e) => {
                    reNew(e, id, data.row.original["id"]);
                  }}
                >
                  <Icon name='refresh' />
                  Renew
                </Button>
              )}
              {data.row.original["renewal-status"] === "not_renewing" && (
                <>
                  <Label circular size='large' color='red'>
                    Not Renewing
                  </Label>
                  <Button
                    className='renewing'
                    secondary
                    size='tiny'
                    onClick={(e) => {
                      reNew(e, id, data.row.original["id"]);
                    }}
                  >
                    <Icon name='refresh' />
                    Renew
                  </Button>
                </>
              )}
              {data.row.original["renewal-status"] === "eligible" && (
                <Button
                  className='renewing'
                  basic
                  size='tiny'
                  onClick={(e) => notRenewing(e, nonRenewalReasons, data.row.original["id"])}
                >
                  I Won’t Be Renewing
                </Button>
              )}
              {data.row.original["renewal-status"] === "ineligible" && renewalWindowStartedAt && (
                <>
                  <p className='subText' style={{ marginBottom: 8, fontSize: 14 }}>
                    {" "}
                    Eligible for renewal starting:{" "}
                  </p>
                  <SafeMoment format='MMM D, YYYY' utc={true}>
                    {renewalWindowStartedAt}
                  </SafeMoment>
                </>
              )}
              {data.row.original["renewal-status"] === "lapsed" && (
                <Label circular size='large' color='red'>
                  Lapsed
                </Label>
              )}
              {data.row.original["renewal-status"] === "active_renewal" && (
                <Label circular size='large' color='blue'>
                  Renewal Submitted
                </Label>
              )}
              {data.row.original["renewal-status"] === "renewed" && (
                <Label circular size='large' color='green'>
                  Renewed
                </Label>
              )}
            </div>
          );
        },
      },
    ],
    [normalizedPermitsTypes],
  );

  const currentPermit: any = params.permitId ? permitsRows.find((item: any) => item.id == params.permitId) : undefined;
  const openRenewalPermit: any = params.parentId
    ? applicationRows.find((item: any) => item["parent-id"] == params.parentId)
    : undefined;
  const completedRenewalPermit: any = params.parentId
    ? permitsRows.find((item: any) => item["parent-id"] == params.parentId)
    : undefined;

  const isNotRenewing: boolean =
    currentPermit && params.permitId ? currentPermit["renewal-status"] === "not_renewing" : false;

  const handleKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement>,
    nextTab: DashboardListTab,
    prevTab: DashboardListTab,
  ) => {
    if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
      event.preventDefault();
      if (event.key === "ArrowRight") {
        setActiveDashboardList(nextTab);
        dashboardTabRefs.current[nextTab]?.focus();
      }
      if (event.key === "ArrowLeft") {
        setActiveDashboardList(prevTab);
        dashboardTabRefs.current[prevTab]?.focus();
      }
    }
  };

  const isLoading = permitsQuery.isLoading || applicationQuery.isLoading || permitsTypesQuery.status === "loading";

  useEffect(() => {
    const { status, permitId, renewing } = queryString.parse(location.search) as any;
    if (status === "active" || status === "renewed") {
      setActiveDashboardList(1);
      return;
    }
    if (permitId && permitsRows) {
      if (renewing === "false") {
        setActiveDashboardList(1);
        const permit = permitsRows.find((item: any) => item.id == permitId);
        if (!permit) {
          return;
        }
        if (permit["renewal-status"] === "not_renewing") {
          return;
        }
        const id: string = permit["permits-type-id"];
        if (!normalizedPermitsTypes.permitsType) {
          return;
        }
        const nonRenewalReasons = _.get(normalizedPermitsTypes.permitsType[id], "attributes.nonRenewalReasons");
        setPermitId(permitId);
        setReasons(nonRenewalReasons);
        setNotReNewingModalState(true);
      }
    }
  }, [applicationQuery.isLoading]);

  if (!isLoggedIn) {
    return <Redirect to={`/${site}/public/permits`} />;
  }

  const handleTabClick = (index: DashboardListTab) => {
    setActiveDashboardList(index);
  };

  return (
    <>
      {showPopup && (
        <CustomContextMenu closePopup={setShowPopup} x={popupPosition.x} y={popupPosition.y} url={contextMenuLink} />
      )}
      <PublicWrapper homeUrl={`/${site}/public/permits`} contentRef={contentRef}>
        <Segment basic className='less-padding-segment-mobile'>
          <NotReNewingModal
            open={notReNewingModalState}
            close={closeNotReNewingModal}
            reasons={reasons}
            onSubmit={(data: any) => onSubmit(data)}
          />
          <Message error hidden={!permitsQuery.isError || !applicationQuery.isError} aria-live='polite'>
            {_.get(permitsQuery, "error.message")}
          </Message>
          <Segment basic>
            <Header as='h1' content='My Dashboard' />
            {(params.status === "active" || isNotRenewing) && openRenewalPermit && (
              <Message
                warning
                icon={<Icon size='tiny' name='times circle outline' />}
                header='This renewal has already been submitted'
                content={
                  <>
                    <a href={`/${site}/public/permits/details/${openRenewalPermit.id}#Application`}>Click here </a>
                    to view the status of your existing renewal application
                  </>
                }
                aria-live='polite'
              />
            )}
            {params.status === "renewed" && completedRenewalPermit && (
              <Message
                warning
                icon={<Icon size='tiny' name='times circle outline' />}
                header='This renewal has already been renewed'
                content={
                  <>
                    <a href={`/${site}/public/permits/details/${completedRenewalPermit.id}#Application`}>Click here </a>
                    to view the renewed application.
                  </>
                }
                aria-live='polite'
              />
            )}
            {params.status === "expired" && (
              <Message
                warning
                header='The renewal window has closed'
                content='The renewal window for this application has closed, and it can no longer be renewed.'
                aria-live='polite'
              />
            )}
            <Segment className='dashboard-tabs'>
              <div role='tablist' className='tab-list' aria-label='Application Types'>
                <button
                  role='tab'
                  aria-selected={activeDashboardList === DashboardListTabs.ALL_PERMITS ? "true" : "false"}
                  tabIndex={activeDashboardList === DashboardListTabs.ALL_PERMITS ? 0 : -1}
                  onClick={() => handleTabClick(DashboardListTabs.ALL_PERMITS)}
                  onKeyDown={(e) => handleKeyDown(e, DashboardListTabs.ISSUED_PERMITS, DashboardListTabs.INSPECTIONS)}
                  ref={(el: HTMLButtonElement) => (dashboardTabRefs.current[DashboardListTabs.ALL_PERMITS] = el)} //Used for managing focus when using arrow keys
                  className={`${activeDashboardList === DashboardListTabs.ALL_PERMITS ? "active" : ""} ${
                    activeDashboardList === DashboardListTabs.ALL_PERMITS ? "tw-font-bold" : "tw-font-normal"
                  }`}
                >
                  All
                </button>
                <button
                  role='tab'
                  aria-selected={activeDashboardList === DashboardListTabs.ISSUED_PERMITS ? "true" : "false"}
                  tabIndex={activeDashboardList === DashboardListTabs.ISSUED_PERMITS ? 0 : -1}
                  onClick={() => handleTabClick(1)}
                  onKeyDown={(e) => handleKeyDown(e, DashboardListTabs.INSPECTIONS, DashboardListTabs.ALL_PERMITS)}
                  ref={(el: HTMLButtonElement) => (dashboardTabRefs.current[DashboardListTabs.ISSUED_PERMITS] = el)} //Used for managing focus when using arrow keys
                  className={`${activeDashboardList === DashboardListTabs.ISSUED_PERMITS ? "active" : ""} ${
                    activeDashboardList === DashboardListTabs.ISSUED_PERMITS ? "tw-font-bold" : "tw-font-normal"
                  }`}
                >
                  Issued
                </button>
                <button
                  role='tab'
                  aria-selected={activeDashboardList === DashboardListTabs.INSPECTIONS ? "true" : "false"}
                  tabIndex={activeDashboardList === DashboardListTabs.INSPECTIONS ? 0 : -1}
                  onClick={() => handleTabClick(2)}
                  onKeyDown={(e) => handleKeyDown(e, DashboardListTabs.ALL_PERMITS, DashboardListTabs.ISSUED_PERMITS)}
                  ref={(el: HTMLButtonElement) => (dashboardTabRefs.current[DashboardListTabs.INSPECTIONS] = el)} //Used for managing focus when using arrow keys
                  className={`${activeDashboardList === DashboardListTabs.INSPECTIONS ? "active" : ""} ${
                    activeDashboardList === DashboardListTabs.INSPECTIONS ? "tw-font-bold" : "tw-font-normal"
                  }`}
                >
                  Inspections
                </button>
              </div>
            </Segment>
            <Segment
              style={{ padding: isLoading ? 16 : 0, marginTop: "16px", overflowX: "auto" }}
              role='tabpanel'
              tabIndex={0}
            >
              {activeDashboardList === DashboardListTabs.INSPECTIONS && <CitizenInspectionList />}
              {(activeDashboardList === DashboardListTabs.ALL_PERMITS ||
                activeDashboardList === DashboardListTabs.ISSUED_PERMITS) && (
                <PlaceholderUntilLoaded isLoading={isLoading} lineCount={5}>
                  {noApplicationRows || noPermitsRows ? (
                    <Message aria-live='polite'>
                      <Message.Header>No Applications Found</Message.Header>
                      <p>{`Your ${
                        activeDashboardList === DashboardListTabs.ALL_PERMITS ? "active" : "completed"
                      } applications will display here.`}</p>
                    </Message>
                  ) : (
                    <VZenTable
                      columns={activeDashboardList === DashboardListTabs.ALL_PERMITS ? columns : permitColumns}
                      data={activeDashboardList === DashboardListTabs.ALL_PERMITS ? applicationRows : permitsRows}
                      initialState={initialState}
                      containerProps={containerProps}
                      tableProps={tableProps}
                    />
                  )}
                </PlaceholderUntilLoaded>
              )}
            </Segment>
          </Segment>
        </Segment>
      </PublicWrapper>
    </>
  );
};

export default CitizenList;
